import { borderRadius, colors, sxPresets } from '@/theme'
import type { InvestigationResult, Investigation, TestResultLine } from '@/types/investigation'
import { ExpandMore } from '@mui/icons-material'
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useState } from 'react'

interface TestResultsProps {
  investigation: Investigation
}

/**
 * Parses results_raw JSON from the PHP backend lab integration.
 *
 * The raw JSON follows a PascalCase structure:
 * { Tests: [{ TestName, TestCode, ResultLines: [{ TestName, Result, UOM, RangeText, Flag, DisplayOrder }] }] }
 *
 * Returns null when the data is corrupted or malformed.
 */
function parseResultsRaw(raw: string): InvestigationResult[] | null {
  if (!raw?.length || raw.trim() === '') return []

  try {
    const parsed = JSON.parse(raw)

    const tests = parsed.Tests
    if (!Array.isArray(tests)) return null

    return tests.map((test: Record<string, unknown>) => {
      const lines = test.ResultLines as Record<string, unknown>[] | undefined
      return {
        testName: String(test.TestName ?? ''),
        testCode: String(test.TestCode ?? ''),
        resultLines: Array.isArray(lines)
          ? lines.map(line => ({
              testName: String(line.TestName ?? ''),
              result: String(line.Result ?? ''),
              UOM: String(line.UOM ?? ''),
              rangeText: String(line.RangeText ?? ''),
              flag: String(line.Flag ?? ''),
              displayOrder: Number(line.DisplayOrder ?? 0)
            }))
          : []
      }
    })
  } catch {
    return null
  }
}

function getResultLines(investigation: Investigation): { lines: TestResultLine[]; isCorrupted: boolean } {
  if (investigation.testResults && investigation.testResults.length > 0) {
    return { lines: investigation.testResults.flatMap(entry => entry.resultLines), isCorrupted: false }
  }

  if (investigation.resultRaw) {
    const parsed = parseResultsRaw(investigation.resultRaw)
    if (parsed === null) {
      return { lines: [], isCorrupted: true }
    }
    if (parsed.length > 0) {
      return { lines: parsed.flatMap(entry => entry.resultLines), isCorrupted: false }
    }
    // Unparseable raw text — show as single entry
    return { lines: [{ testName: 'Raw results', result: investigation.resultRaw }], isCorrupted: false }
  }

  return { lines: [], isCorrupted: false }
}

function formatResultValue(line: TestResultLine): string {
  let value = line.result
  if (line.UOM && line.UOM.trim() !== '') value += ` ${line.UOM}`
  if (line.rangeText && line.rangeText.trim() !== '') value += ` (${line.rangeText})`
  if (line.flag && line.flag.trim() !== '') value += ` [${line.flag}]`
  return value
}

export default function TestResults({ investigation }: TestResultsProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const { lines: resultLines, isCorrupted } = getResultLines(investigation)

  return (
    <Paper id='test-results' elevation={0} sx={{ border: `1px solid ${colors.border.default}` }}>
      <Box sx={{ ...sxPresets().section.header, cursor: 'pointer' }} onClick={() => setIsExpanded(!isExpanded)}>
        <Typography variant='sectionTitle'>Test results</Typography>
        <IconButton
          size='small'
          sx={{ ...sxPresets().expandButton, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <ExpandMore />
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={sxPresets().section.content}>
          {isCorrupted ? (
            <Alert severity='error'>Test results data is corrupted or malformed. Please contact support.</Alert>
          ) : (
            <TableContainer
              sx={{ border: `1px solid ${colors.border.default}`, borderRadius: borderRadius.lg, overflow: 'hidden' }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: colors.brand.primaryLight }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Results</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultLines.length > 0 ? (
                    resultLines.map((line, index) => (
                      <TableRow key={index} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>{line.testName}</TableCell>
                        <TableCell>{formatResultValue(line)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant='body2'>No test results available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}
